import { useState } from "react";
import { motion } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { apiFetch } from "../utils/api";

export interface OpenRole {
  id: string;
  title: string;
  company: string;
  department: string;
  type: "Full-Time" | "Hybrid" | "Remote";
  location: string;
  experience: string;
  salary: string;
  summary: string;
  responsibilities: { title: string; desc: string }[];
  requirements: string[];
  compensationDetails: string[];
  techStack: string[];
}

export const openRoles: OpenRole[] = [
  {
    id: "uiux-dev",
    title: "UI/UX Designer & Developer",
    company: "Radiece Private Limited",
    department: "Software & Digital Platforms",
    type: "Remote",
    location: "Remote (Work from Home)",
    experience: "1–2 Years",
    salary: "₹13,000 – ₹20,000 per month",
    summary: "Radiece Private Limited is hiring a UI/UX Designer & Developer with 1–2 years of experience. This role entails designing user interfaces for a variety of software products—including web applications, mobile apps, dashboards, and digital tools—translating designs into clean front-end code, and supporting company-wide graphic design and digital marketing requirements.",
    responsibilities: [
      {
        title: "Software Product UI/UX Design",
        desc: "Design end-to-end user interfaces, navigation flows, wireframes, and interactive prototypes for diverse software products, SaaS platforms, mobile applications, and administrative dashboards."
      },
      {
        title: "Front-End Development",
        desc: "Convert approved UI mockups into structured, responsive, and cross-browser-compatible code using HTML5, CSS3, Tailwind CSS/Bootstrap, and JavaScript."
      },
      {
        title: "Marketing & Graphic Design Support",
        desc: "Create digital marketing posters, social media banners, promotional graphics, corporate presentation decks, and branding collateral."
      },
      {
        title: "Design Systems & Consistency",
        desc: "Build and maintain scalable design systems, UI component libraries, and style guides across all software products and corporate assets."
      },
      {
        title: "Technical Collaboration",
        desc: "Work with software engineers and project stakeholders to evaluate technical feasibility, hand off assets, and ensure high-fidelity implementation."
      }
    ],
    requirements: [
      "1–2 years of professional experience in UI/UX design for software applications and responsive front-end development.",
      "High proficiency in Figma or Adobe XD (components, auto-layout, design tokens, and interactive prototyping).",
      "Proficiency in Adobe Photoshop, Illustrator, or Canva for digital marketing assets and vector illustrations.",
      "Practical knowledge of HTML5, CSS3, Tailwind CSS or Bootstrap, and core JavaScript.",
      "Strong understanding of user flow architecture, information hierarchy, system usability, and interface interaction design across various software platforms.",
      "Mandatory Portfolio Requirement: A portfolio showcasing complete software UI/UX workflows, live/coded web interfaces, and graphic design samples is mandatory.",
      "Remote Work Setup: Dedicated home workspace with a reliable computer system and stable high-speed internet."
    ],
    compensationDetails: [
      "Monthly CTC: ₹13,000 – ₹20,000 (based on technical skill and portfolio review)",
      "Work Mode: 100% Remote / Work from Home",
      "Work Hours: Standard full-time schedule (Monday to Friday)"
    ],
    techStack: ["Figma", "Adobe XD", "HTML5", "CSS3", "Tailwind CSS", "Bootstrap", "JavaScript", "Photoshop", "Illustrator", "Canva"]
  }
];

const culturePillars = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
    title: "Engineering-Led Discipline",
    desc: "We write clean, verified, maintainable code with strict type safety, zero suppression policy, and automated test coverage across software and physical computing."
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: "Flexible Autonomy & Deep Work",
    desc: "Asynchronous-first communication and focused workflows designed to give creative and engineering talent uninterrupted deep-work flow."
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5.032 14.47A4.5 4.5 0 008.212 22h7.576a4.5 4.5 0 003.18-7.53l-4.06-4.061a2.25 2.25 0 01-.658-1.591V3.104M9.75 3.104c-.251.037-.502.082-.75.137M9.75 3.104h4.5m0 0c.25.037.5.082.75.137M12 15.75h.008v.008H12v-.008z" />
      </svg>
    ),
    title: "Software & Hardware Synergy",
    desc: "Work on genuine deep-tech systems spanning enterprise platforms (RaBuOS), embedded electronics, robotics, and high-fidelity product interfaces."
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-5.25 6.557c0 1.5 1.5 3 5.25 3s5.25-1.5 5.25-3m-10.5 0h10.5" />
      </svg>
    ),
    title: "End-to-End Craftsmanship",
    desc: "From scalable server backends and deterministic microcontrollers to pixel-accurate design systems and brand identity assets."
  }
];

const hiringSteps = [
  {
    step: "01",
    title: "Application & Profile Review",
    desc: "Our practice leads review your technical background, resume, and relevant project/portfolio artifacts within 48 business hours."
  },
  {
    step: "02",
    title: "Technical & Domain Discovery",
    desc: "A 45-minute conversation discussing system architecture, engineering methodologies, problem-solving approach, and past project experience."
  },
  {
    step: "03",
    title: "Practical Problem Evaluation",
    desc: "A focused, real-world domain challenge or practical implementation scenario — no arbitrary or unrealistic puzzle questions."
  },
  {
    step: "04",
    title: "Culture, Alignment & Offer",
    desc: "Meet practice leadership to align on role expectations, working structure, and growth roadmap, followed by a formal corporate offer."
  }
];

export function Careers() {
  const currentRole = openRoles[0]!;

  // Form State
  const [applicantName, setApplicantName] = useState("");
  const [applicantEmail, setApplicantEmail] = useState("");
  const [applicantPhone, setApplicantPhone] = useState("");
  const [portfolioLink, setPortfolioLink] = useState("");
  const [githubLink, setGithubLink] = useState("");
  const [experienceLevel, setExperienceLevel] = useState("1-2 Years");
  const [primaryTools, setPrimaryTools] = useState("Figma, Tailwind CSS, HTML/CSS, Photoshop");
  const [coverNote, setCoverNote] = useState("");

  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleApplyClick = () => {
    const formElem = document.getElementById("apply-form");
    if (formElem) {
      formElem.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicantName || !applicantEmail || !portfolioLink || !coverNote) {
      setErrorMsg("Please fill in all required fields including your mandatory portfolio link.");
      setStatus("error");
      return;
    }

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await apiFetch("/api/contact", {
        method: "POST",
        body: JSON.stringify({
          name: applicantName,
          email: applicantEmail,
          phone: applicantPhone,
          organizationType: `Career Applicant — UI/UX Designer & Developer (${experienceLevel})`,
          service: "Career Application",
          message: `JOB APPLICATION DETAILS:
Position Applied: UI/UX Designer & Developer (Remote)
Candidate Name: ${applicantName}
Email: ${applicantEmail}
Phone / WhatsApp: ${applicantPhone || "Not Provided"}
Experience Level: ${experienceLevel}
Primary Tools: ${primaryTools}

PORTFOLIO LINK (Mandatory):
${portfolioLink}

GITHUB / LIVE PROJECTS LINK:
${githubLink || "Not Provided"}

COVER NOTE & EXPERIENCE SUMMARY:
${coverNote}`,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to submit application. Please try again.");
      }

      setStatus("sent");
      setApplicantName("");
      setApplicantEmail("");
      setApplicantPhone("");
      setPortfolioLink("");
      setGithubLink("");
      setCoverNote("");
    } catch (err: any) {
      setErrorMsg(err.message || "Something went wrong. Please check your internet connection and try again.");
      setStatus("error");
    }
  };

  return (
    <div className="no-print bg-white dark:bg-[#090e17] min-h-screen transition-colors">
      {/* Hero Banner */}
      <PageHero
        badge="Current Opening • UI/UX Designer & Developer"
        badgeDotColor="bg-emerald-400"
        title="Careers at RADIECE"
        subtitle="Build High-Impact Digital & Deep-Tech Systems"
        description="Join an engineering-driven company where design and code unite. We are currently hiring for remote engineering & design talent."
        maxWidth="max-w-5xl"
      >
        <motion.button
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.96 }}
          onClick={handleApplyClick}
          className="btn-shine bg-primary hover:bg-sky-400 text-slate-950 font-bold px-6 py-2.5 rounded-full transition-all shadow-md cursor-pointer"
        >
          Apply for Open Role →
        </motion.button>
        <motion.a
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.96 }}
          href="#culture"
          className="bg-white/10 hover:bg-white/20 text-white font-medium border border-white/20 px-6 py-2.5 rounded-full transition-colors"
        >
          Work Culture & Philosophy
        </motion.a>
        <motion.a
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.96 }}
          href="#apply-form"
          className="bg-white/10 hover:bg-white/20 text-white font-medium border border-white/20 px-6 py-2.5 rounded-full transition-colors"
        >
          Direct Application Form
        </motion.a>
      </PageHero>

      {/* Engineering & Design Culture Pillars */}
      <section id="culture" className="py-16 sm:py-20 bg-slate-50 dark:bg-[#070b12] border-b border-slate-200 dark:border-slate-800 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Our Operating Philosophy</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
              Why engineers & creators thrive at RADIECE.
            </h2>
            <p className="mt-3 text-slate-600 dark:text-slate-300 text-base leading-relaxed">
              We provide an environment grounded in deep-tech innovation, architectural rigor, and the autonomy required to produce exceptional work.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {culturePillars.map((pillar, idx) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="card-interactive bg-white dark:bg-[#0e1724] border border-slate-200/90 dark:border-slate-800 rounded-2xl p-6 sm:p-7 shadow-xs hover:border-slate-300 dark:hover:border-slate-700 flex flex-col justify-between transition-colors"
              >
                <div>
                  <div className="w-12 h-12 bg-brand/10 dark:bg-primary/15 text-brand dark:text-primary rounded-xl flex items-center justify-center mb-5 border border-brand/20 dark:border-primary/30">
                    {pillar.icon}
                  </div>
                  <h3 className="text-lg font-bold text-brand-dark dark:text-slate-100 mb-2">{pillar.title}</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{pillar.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Job Opening Section */}
      <section id="open-positions" className="py-16 sm:py-24 bg-white dark:bg-[#0b131e] transition-colors">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Available Opportunity</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
                Current Job Opening
              </h2>
            </div>
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-3.5 py-1.5 rounded-full self-start md:self-auto">
              1 Active Position
            </span>
          </div>

          {/* Job Card */}
          <div className="card-interactive bg-slate-50 dark:bg-[#0e1724] rounded-2xl border border-brand/30 dark:border-primary/30 shadow-md ring-1 ring-brand/15 dark:ring-primary/20 p-6 sm:p-8 hover:border-brand/50 dark:hover:border-primary/50 transition-colors">
            {/* Header Badge Row */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-6 border-b border-slate-200 dark:border-slate-800">
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-2.5">
                  <span className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-xs font-extrabold px-3 py-1 rounded-full border border-emerald-500/30 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    Actively Hiring
                  </span>
                  <span className="bg-primary/10 text-brand dark:text-primary text-xs font-bold px-3 py-1 rounded-full border border-primary/20 dark:border-primary/30">
                    {currentRole.department}
                  </span>
                  <span className="bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-medium px-3 py-1 rounded-full">
                    {currentRole.type}
                  </span>
                  <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                    📍 {currentRole.location}
                  </span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-extrabold text-brand-dark dark:text-slate-100">
                  {currentRole.title}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1">
                  Company: <strong className="text-slate-700 dark:text-slate-200">{currentRole.company}</strong> • Experience Required: <strong className="text-slate-700 dark:text-slate-200">{currentRole.experience}</strong>
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 shrink-0">
                <div className="bg-slate-900 dark:bg-slate-950 text-white rounded-xl px-4 py-2.5 border border-slate-800 dark:border-slate-700 text-left sm:text-right">
                  <span className="text-[11px] text-slate-400 block uppercase tracking-wider">Salary CTC</span>
                  <span className="text-sm font-extrabold text-primary font-mono">{currentRole.salary}</span>
                </div>
                <button
                  onClick={handleApplyClick}
                  className="px-6 py-3 rounded-xl bg-brand dark:bg-primary text-white dark:text-slate-950 hover:bg-brand-dark dark:hover:bg-sky-400 font-bold text-xs uppercase tracking-wider transition-all shadow-md cursor-pointer shrink-0"
                >
                  Apply Now →
                </button>
              </div>
            </div>

            {/* Role Overview */}
            <div className="py-6 border-b border-slate-200 dark:border-slate-800">
              <h4 className="text-xs font-bold uppercase tracking-wider text-brand dark:text-primary mb-2">
                Role Overview
              </h4>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                {currentRole.summary}
              </p>
            </div>

            {/* Key Responsibilities */}
            <div className="py-6 border-b border-slate-200 dark:border-slate-800">
              <h4 className="text-xs font-bold uppercase tracking-wider text-brand dark:text-primary mb-4">
                Key Responsibilities
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentRole.responsibilities.map((resp, idx) => (
                  <div key={idx} className="bg-white dark:bg-[#121f2f] p-4 rounded-xl border border-slate-200/80 dark:border-slate-800">
                    <h5 className="text-xs sm:text-sm font-bold text-brand-dark dark:text-slate-100 mb-1">
                      {resp.title}
                    </h5>
                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-light">
                      {resp.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Requirements & Qualifications */}
            <div className="py-6 border-b border-slate-200 dark:border-slate-800">
              <h4 className="text-xs font-bold uppercase tracking-wider text-brand dark:text-primary mb-4">
                Requirements & Qualifications
              </h4>
              <ul className="space-y-2.5">
                {currentRole.requirements.map((req, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                    <span className="text-emerald-500 font-bold text-base mt-[-2px]">✓</span>
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Compensation & Work Structure */}
            <div className="py-6 border-b border-slate-200 dark:border-slate-800">
              <h4 className="text-xs font-bold uppercase tracking-wider text-brand dark:text-primary mb-3">
                Compensation & Work Structure
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {currentRole.compensationDetails.map((detail, idx) => (
                  <div key={idx} className="p-3 bg-white dark:bg-[#121f2f] rounded-xl border border-slate-200/80 dark:border-slate-800 text-xs text-slate-700 dark:text-slate-300 font-medium">
                    {detail}
                  </div>
                ))}
              </div>
            </div>

            {/* Tech Stack Tags & Apply Button */}
            <div className="pt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block mb-2">
                  Primary Toolset:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {currentRole.techStack.map((tech) => (
                    <span
                      key={tech}
                      className="bg-white dark:bg-[#121f2f] border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-md px-2.5 py-1 text-xs font-mono font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <button
                onClick={handleApplyClick}
                className="bg-primary hover:bg-sky-400 text-slate-950 font-bold px-6 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-sm cursor-pointer shrink-0"
              >
                Proceed to Application Form ↓
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 4-Step Hiring Process */}
      <section className="py-16 sm:py-20 bg-slate-50 dark:bg-[#070b12] border-t border-b border-slate-200 dark:border-slate-800 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Our Interview Flow</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
              A transparent, respectful hiring process.
            </h2>
            <p className="mt-3 text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed">
              We respect your time. Our hiring process is designed to be fast, transparent, and focused on practical technical competence and team alignment.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {hiringSteps.map((s) => (
              <div key={s.step} className="bg-white dark:bg-[#0e1724] rounded-2xl p-6 sm:p-7 border border-slate-200/90 dark:border-slate-800 shadow-xs relative transition-colors">
                <span className="text-3xl font-extrabold text-brand/20 dark:text-primary/30 font-mono block mb-2">{s.step}</span>
                <h3 className="text-base font-bold text-brand-dark dark:text-slate-100 mb-2">{s.title}</h3>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="apply-form" className="py-16 sm:py-24 bg-slate-900 text-white scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 bg-primary/20 border border-primary/30 text-primary text-xs font-bold rounded-full uppercase tracking-wider mb-3">
              Application Form
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Apply for UI/UX Designer & Developer
            </h2>
            <p className="mt-3 text-slate-300 text-sm sm:text-base max-w-xl mx-auto leading-relaxed font-light">
              Submit your resume, portfolio link, and technical background. Submissions without portfolio links will not be reviewed.
            </p>
          </div>

          <div className="bg-[#101c2a] rounded-2xl p-6 sm:p-10 border border-white/15 shadow-2xl">
            {status === "sent" ? (
              <div className="text-center py-12 space-y-4">
                <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-2 border border-emerald-500/30">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">Application Received!</h3>
                <p className="text-slate-300 text-sm max-w-md mx-auto leading-relaxed font-light">
                  Thank you for applying to RADIECE PRIVATE LIMITED for the UI/UX Designer & Developer role. Our team will review your portfolio and get in touch within 48 business hours.
                </p>
                <div className="pt-4">
                  <button
                    onClick={() => setStatus("idle")}
                    className="inline-flex items-center px-6 py-2.5 rounded-lg bg-primary text-slate-950 font-bold text-xs uppercase tracking-wider hover:bg-sky-400 transition-colors"
                  >
                    Submit Another Application
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="applicant-name" className="block text-xs font-semibold text-slate-200 mb-1.5 uppercase tracking-wider">
                      Full Name *
                    </label>
                    <input
                      id="applicant-name"
                      type="text"
                      required
                      value={applicantName}
                      onChange={(e) => setApplicantName(e.target.value)}
                      placeholder="e.g. Rahul Sharma"
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>

                  <div>
                    <label htmlFor="applicant-email" className="block text-xs font-semibold text-slate-200 mb-1.5 uppercase tracking-wider">
                      Email Address *
                    </label>
                    <input
                      id="applicant-email"
                      type="email"
                      required
                      value={applicantEmail}
                      onChange={(e) => setApplicantEmail(e.target.value)}
                      placeholder="rahul@example.com"
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="applicant-phone" className="block text-xs font-semibold text-slate-200 mb-1.5 uppercase tracking-wider">
                      Phone Number / WhatsApp *
                    </label>
                    <input
                      id="applicant-phone"
                      type="tel"
                      required
                      value={applicantPhone}
                      onChange={(e) => setApplicantPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>

                  <div>
                    <label htmlFor="applicant-exp" className="block text-xs font-semibold text-slate-200 mb-1.5 uppercase tracking-wider">
                      Years of Experience *
                    </label>
                    <select
                      id="applicant-exp"
                      value={experienceLevel}
                      onChange={(e) => setExperienceLevel(e.target.value)}
                      className="w-full px-4 py-2.5 bg-[#101c2a] border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                    >
                      <option value="1-2 Years">1 – 2 Years (Required Range)</option>
                      <option value="2-3 Years">2 – 3 Years</option>
                      <option value="3+ Years">3+ Years</option>
                      <option value="< 1 Year with Strong Portfolio">&lt; 1 Year (with Strong Portfolio)</option>
                    </select>
                  </div>
                </div>

                {/* Mandatory Portfolio Link */}
                <div className="p-4 bg-primary/10 border border-primary/30 rounded-xl">
                  <label htmlFor="applicant-portfolio" className="block text-xs font-bold text-primary mb-1.5 uppercase tracking-wider">
                    Portfolio Link (Behance / Dribbble / Figma / Website) *
                  </label>
                  <input
                    id="applicant-portfolio"
                    type="url"
                    required
                    value={portfolioLink}
                    onChange={(e) => setPortfolioLink(e.target.value)}
                    placeholder="https://behance.net/username or Figma prototype URL"
                    className="w-full px-4 py-2.5 bg-black/40 border border-white/25 rounded-lg text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary font-mono"
                  />
                  <p className="text-[11px] text-slate-300 mt-1.5">
                    * Mandatory. Submissions without links to software UI/UX workflows, live web interfaces, or graphic samples will not be reviewed.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="applicant-github" className="block text-xs font-semibold text-slate-200 mb-1.5 uppercase tracking-wider">
                      GitHub or Live Coded Projects URL (Optional)
                    </label>
                    <input
                      id="applicant-github"
                      type="url"
                      value={githubLink}
                      onChange={(e) => setGithubLink(e.target.value)}
                      placeholder="https://github.com/username or live site"
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary font-mono"
                    />
                  </div>

                  <div>
                    <label htmlFor="applicant-tools" className="block text-xs font-semibold text-slate-200 mb-1.5 uppercase tracking-wider">
                      Primary Tools & Stack
                    </label>
                    <input
                      id="applicant-tools"
                      type="text"
                      value={primaryTools}
                      onChange={(e) => setPrimaryTools(e.target.value)}
                      placeholder="e.g. Figma, Tailwind CSS, HTML/CSS, Photoshop"
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="applicant-note" className="block text-xs font-semibold text-slate-200 mb-1.5 uppercase tracking-wider">
                    Cover Note & Experience Summary *
                  </label>
                  <textarea
                    id="applicant-note"
                    rows={4}
                    required
                    value={coverNote}
                    onChange={(e) => setCoverNote(e.target.value)}
                    placeholder="Briefly describe your experience designing software products, web applications, your front-end development capabilities, and why you are interested in this remote role..."
                    className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 text-sm focus:outline-none focus:border-primary resize-y"
                  ></textarea>
                </div>

                {status === "error" && (
                  <div className="p-3.5 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300 text-xs font-medium">
                    ⚠️ {errorMsg}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="btn-shine w-full bg-primary hover:bg-sky-400 disabled:opacity-50 text-slate-950 font-bold py-3.5 rounded-lg text-xs sm:text-sm uppercase tracking-wider cursor-pointer shadow-lg"
                >
                  {status === "sending" ? "Submitting Career Application..." : "Submit UI/UX Application →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
